'use client'

import { ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { Fragment, useMemo, useRef, useState } from 'react'
import { useGetCitiesQuery } from '@/api/cities/get-cities-query'
import { ICity } from '@/api/cities/types/cities-types'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Middot } from '@/components/middot'
import { Skeleton } from '@/components/skeleton'
import { useCitiesFinderContext } from '@/features/cities/cities-finder/cities-finder-context'
import { MapLabel } from '@/features/cities/map-label'
import { SvgOutlinedSearch } from '@/icons/svg-outlined-search'
import { Locale } from '@/setup/locales'
import { getLinkHref } from '@/setup/routing'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

interface ICitiesDirectoryProps {
    /** Adjusts sticky offsets: full page scroll vs inside the sliding pane */
    variant?: 'page' | 'pane'
    /** Sticky top offset of the pane header, e.g. below the sheet grabber */
    stickyHeaderClassName?: string
    /** Anchor margin of the country sections, must clear the pinned header(s) */
    sectionScrollMarginClassName?: string
    selectedCitySlug?: string
    onAfterSelect?: () => void
    className?: string
}

interface ICountrySection {
    name: string
    emoji: string | null
    cities: ICity[]
}

/**
 * All cities in one flow: country chips on top as anchors, a client-side
 * search, then per-country sections with single-column city rows. Used on
 * the cities page and inside the city switcher pane.
 */
export const CitiesDirectory = ({
    variant = 'page',
    stickyHeaderClassName = 'top-0',
    sectionScrollMarginClassName,
    selectedCitySlug,
    onAfterSelect,
    className,
}: ICitiesDirectoryProps) => {
    const t = useTranslations()
    const locale = useLocale()

    const { data, isLoading } = useGetCitiesQuery({})

    const [query, setQuery] = useState('')
    const [activeCountry, setActiveCountry] = useState<string | null>(null)
    const [loadingCitySlug, setLoadingCitySlug] = useState<string | null>(null)

    // No-op outside the cities finder context (e.g. in the switcher pane)
    const { setHoveredCitySlug } = useCitiesFinderContext()

    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})

    const normalizedQuery = query.trim().toLowerCase()

    const countrySections = useMemo<ICountrySection[]>(() => {
        const allCities = data?.data

        if (!allCities) return []

        const record: Record<string, ICountrySection> = {}

        const sortedCities = [...allCities].sort((a, b) => a.name.localeCompare(b.name))

        for (const city of sortedCities) {
            if (!city.country?.name) continue

            if (normalizedQuery && !city.name.toLowerCase().includes(normalizedQuery)) continue

            record[city.country.name] ??= {
                name: city.country.name,
                emoji: city.country.emoji ?? null,
                cities: [],
            }

            record[city.country.name].cities.push(city)
        }

        return Object.values(record).sort((a, b) => b.cities.length - a.cities.length)
    }, [data, normalizedQuery])

    const isSearching = Boolean(normalizedQuery)

    const onCountryChipClick = (countryName: string) => {
        setActiveCountry(countryName)

        sectionRefs.current[countryName]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <div className={cn('flex w-full flex-col', className)}>
            {/* In the pane and modal the search sticks together with the chips,
                on the page only the chips pin (the search scrolls with the intro) */}
            <div
                className={cn(
                    variant === 'pane' &&
                        cn(
                            'sticky z-[2] bg-bg-neutral-base pt-4 px-4 border-b border-b-border-primary',
                            stickyHeaderClassName
                        )
                )}
            >
                {/* Search */}
                <div className="relative w-full">
                    {/* The Svg components ignore className, position via wrapper */}
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 flex pointer-events-none">
                        <SvgOutlinedSearch color={COLORS.fg.tertiary} size={18} />
                    </span>

                    <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={t('citySearch.searchPlaceholder')}
                        aria-label={t('citySearch.searchPlaceholder')}
                        className="h-12 w-full rounded-full border border-border-primary bg-bg-neutral-base pl-11 pr-4
                        text-p-sm text-fg-primary placeholder:text-fg-tertiary outline-none transition-colors
                        duration-150 focus:border-fg-primary"
                    />
                </div>

                {/* Country chips */}
                {!isSearching && countrySections.length > 1 && (
                    <div
                        className={cn(
                            // Top spacing lives inside the sticky block so the background
                            // covers it while pinned instead of showing a gap
                            'z-[2] bg-bg-neutral-base pt-4 pb-2',
                            variant === 'page' && 'sticky top-16 md:top-0'
                        )}
                    >
                        {/* Bleeds into the surrounding gutter so the scrolling row reaches the edge */}
                        <div
                            className="flex items-center gap-x-2 overflow-x-auto scrollbar-hide -mx-4
                                w-[calc(100%+32px)] px-4"
                        >
                            {countrySections.map((country, index) => {
                                const isActive =
                                    country.name === (activeCountry ?? countrySections[0]?.name)

                                return (
                                    <Fragment key={'cities-directory-anchor' + country.name}>
                                        {index > 0 && <Middot className="shrink-0" />}

                                        <button
                                            type="button"
                                            onClick={() => onCountryChipClick(country.name)}
                                            className={cn(
                                                `shrink-0 py-2 text-label-md whitespace-nowrap cursor-pointer
                                                select-none webkit-tap-transparent outline-none
                                                transition-colors duration-150`,
                                                isActive
                                                    ? 'text-fg-primary underline underline-offset-8'
                                                    : 'text-fg-tertiary hover:text-fg-primary'
                                            )}
                                        >
                                            {country.name}
                                        </button>
                                    </Fragment>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Sections */}
            <div className={cn('mt-4 flex flex-col gap-y-8', variant === 'pane' && 'px-4')}>
                {isLoading &&
                    Array.from({ length: 8 }, (_, index) => (
                        <Skeleton
                            key={'cities-directory-skeleton' + index}
                            className="h-12 w-full rounded-m"
                        />
                    ))}

                {!isLoading && isSearching && !countrySections.length && (
                    <p className="text-p-md text-fg-tertiary">{t('citySearch.noResults')}</p>
                )}

                {countrySections.map((country) => (
                    <div
                        key={'cities-directory-section' + country.name}
                        ref={(element) => {
                            sectionRefs.current[country.name] = element
                        }}
                        className={cn(
                            'flex flex-col',
                            sectionScrollMarginClassName ??
                                (variant === 'page'
                                    ? 'scroll-mt-32 md:scroll-mt-16'
                                    : 'scroll-mt-32')
                        )}
                    >
                        <h3 className="text-h4 mb-2 flex items-center gap-x-2">
                            {country.emoji && <span aria-hidden>{country.emoji}</span>}
                            {country.name}
                        </h3>

                        <div className="flex flex-col divide-y divide-border-secondary">
                            {country.cities.map((city) => {
                                const isSelected = city.slug === selectedCitySlug
                                const restaurantCount = city.numberOfActiveRestaurants

                                return (
                                    <Link
                                        key={'cities-directory-city' + city.slug}
                                        href={getLinkHref(
                                            {
                                                pattern: '/restaurants/[citySlug]',
                                                params: { citySlug: city.slug },
                                            },
                                            locale as Locale
                                        )}
                                        onClick={() => {
                                            setLoadingCitySlug(city.slug)
                                            onAfterSelect?.()
                                        }}
                                        onMouseEnter={() => setHoveredCitySlug(city.slug)}
                                        onMouseLeave={() => setHoveredCitySlug(null)}
                                        className={cn(
                                            `flex h-12 items-center justify-between px-3 -mx-3
                                            transition-colors duration-150 webkit-tap-transparent`,
                                            isSelected
                                                ? 'bg-bg-neutral-surface font-semibold'
                                                : 'hover:bg-bg-neutral-surface'
                                        )}
                                    >
                                        <span className="flex items-center gap-x-2 text-p-md">
                                            {city.name}
                                            <MapLabel label={city.label} />
                                        </span>

                                        <span className="flex items-center gap-x-1.5 text-p-xs text-fg-tertiary">
                                            {restaurantCount > 0 && <span>{restaurantCount}</span>}

                                            {loadingCitySlug === city.slug ? (
                                                <LoadingSpinner
                                                    size={16}
                                                    color={COLORS.fg.tertiary}
                                                />
                                            ) : (
                                                <ChevronRight className="size-[18px] shrink-0 text-fg-disabled" />
                                            )}
                                        </span>
                                    </Link>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
